/**
 * プレーンテキストを安全に描画し、http(s) URL のみ <a> リンクに変換するコンポーネント。
 *
 * - dangerouslySetInnerHTML は使わず、React ノードとして描画する（XSS 対策）。
 * - URL 正規表現で分割し、マッチ部分だけ <a target=_blank rel=noopener noreferrer> にする。
 * - 改行は whitespace-pre-wrap で保持する。
 */
import { Fragment, type ReactNode } from 'react';

/** http(s) URL のマッチ。末尾の句読点・閉じ括弧は URL から除外する。 */
const URL_REGEX = /(https?:\/\/[^\s<]+[^\s<.,!?;:"')\]])/g;

export function LinkifiedText({
  text,
  className,
}: {
  text: string;
  className?: string;
}) {
  return (
    <span className={['whitespace-pre-wrap break-words', className].filter(Boolean).join(' ')}>
      {linkify(text)}
    </span>
  );
}

/** 文字列を URL とテキストに分割し、URL は <a> に変換した React ノード配列を返す。 */
function linkify(text: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  // 正規表現の lastIndex 状態を毎回リセット（グローバル正規表現の共有対策）。
  URL_REGEX.lastIndex = 0;
  let key = 0;
  while ((match = URL_REGEX.exec(text)) !== null) {
    const url = match[0];
    const start = match.index;
    if (start > lastIndex) {
      nodes.push(<Fragment key={key++}>{text.slice(lastIndex, start)}</Fragment>);
    }
    nodes.push(
      <a
        key={key++}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-[var(--accent)] underline break-all hover:opacity-80"
      >
        {url}
      </a>,
    );
    lastIndex = start + url.length;
  }
  if (lastIndex < text.length) {
    nodes.push(<Fragment key={key++}>{text.slice(lastIndex)}</Fragment>);
  }
  return nodes;
}
